import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { Empty, ErrorBox, Loading, Modal } from '../../ui/kit.tsx';
import { SearchBox } from '../../ui/SearchBox.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { CapabilityInfo, CustomRoleInfo, Membership, RoleInfo, RolesCatalog, StudioPerson, TenancyOverview } from './types.ts';
import { ConfirmButton, since } from './ui.tsx';

interface ScopeOption {
  key: string;
  label: string;
  organizationRef: string;
  scope: { organizationRef: string; brandRef?: string; siteRef?: string };
}

const scopeLabel = (m: Membership) => (m.site ? `${m.site.name}` : m.brand ? `${m.brand.name} (brand)` : `${m.organization.name} (organization)`);

/** Roles you can pick for a scope: the templates plus that organization's custom roles. */
function rolesFor(catalog: RolesCatalog | undefined, organizationRef: string): RoleInfo[] {
  if (!catalog) return [];
  return [...catalog.templates, ...catalog.customRoles.filter((r) => r.organization.ref === organizationRef)];
}

const LEVEL_MARK: Record<string, string> = { read: 'R', write: 'W', approve: 'A' };

/** People, their roles per organization, brand or site, and custom roles. Adding a role saves at once; taking access away asks once. */
export function Users() {
  const { query, setQuery } = useRouter();
  const tab = query.get('tab') === 'roles' ? 'roles' : 'people';
  const changes = useChanges();
  const catalog = useOp<RolesCatalog>('admin.roles.list', {}, changes);
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>People</h1>
          <p className="muted">Who can sign in to Studio and what they may do. A person can hold several roles on an organization, a brand or a site; what they may do there is everything those roles allow together.</p>
        </div>
      </header>
      <div className="segmented tabs" role="tablist" aria-label="People and roles">
        <button type="button" role="tab" aria-selected={tab === 'people'} aria-pressed={tab === 'people'} onClick={() => setQuery({ tab: null })}>
          People
        </button>
        <button type="button" role="tab" aria-selected={tab === 'roles'} aria-pressed={tab === 'roles'} onClick={() => setQuery({ tab: 'roles' })}>
          Roles
        </button>
      </div>
      {catalog.error ? <ErrorBox error={catalog.error} onRetry={catalog.reload} /> : null}
      {tab === 'people' ? <PeopleTab catalog={catalog.data ?? undefined} /> : <RolesTab catalog={catalog.data ?? undefined} loading={catalog.loading} />}
    </section>
  );
}

function PeopleTab({ catalog }: { catalog: RolesCatalog | undefined }) {
  const { query, setQuery } = useRouter();
  const changes = useChanges();
  const toast = useToast();
  const q = query.get('q') ?? '';
  const people = useOp<{ users: StudioPerson[] }>('admin.users.list', { q: q || undefined }, changes);
  const overview = useOp<TenancyOverview>('admin.tenancy.overview');

  const scopes = useMemo<ScopeOption[]>(() => {
    const out: ScopeOption[] = [];
    for (const org of overview.data?.organizations ?? []) {
      if (org.canManagePeople) out.push({ key: org.ref, label: `${org.name}: whole organization`, organizationRef: org.ref, scope: { organizationRef: org.ref } });
      for (const brand of org.brands) {
        if (brand.canManagePeople) out.push({ key: brand.ref, label: `${org.name} › ${brand.name}: all its sites`, organizationRef: org.ref, scope: { organizationRef: org.ref, brandRef: brand.ref } });
        for (const site of brand.sites) if (site.canManagePeople) out.push({ key: site.ref, label: `${org.name} › ${site.name}`, organizationRef: org.ref, scope: { organizationRef: org.ref, siteRef: site.ref } });
      }
    }
    return out;
  }, [overview.data]);

  const [scopeKey, setScopeKey] = useState<string>('');
  useEffect(() => {
    if (!scopeKey && scopes[0]) setScopeKey(scopes[0].key);
  }, [scopes, scopeKey]);
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState<string[]>(['content_editor']);
  const target = scopes.find((s) => s.key === scopeKey);
  const choices = rolesFor(catalog, target?.organizationRef ?? '');

  const invite = async (event: FormEvent) => {
    event.preventDefault();
    if (!target || picked.length === 0) return;
    const names = choices.filter((r) => picked.includes(r.key)).map((r) => r.name).join(', ');
    const result = await runWrite<{ newUser: boolean }>(toast, 'admin.users.invite', { email, roles: picked, scope: target.scope }, (r) => `${email} ${r.newUser ? 'invited' : 'added'} as ${names}`);
    if (result) setEmail('');
  };

  return (
    <>
      {scopes.length ? (
        <form className="card invite-form" onSubmit={invite} aria-label="Invite">
          <div className="inline-form">
            <label className="grow">
              Invite by email
              <input type="email" required value={email} placeholder="name@company.com" onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="inline-field scope-picker">
              <span className="muted">to</span>
              <select aria-label="Access to" value={scopeKey} onChange={(e) => setScopeKey(e.target.value)}>
                {scopes.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className="btn btn-primary" disabled={picked.length === 0}>
              Invite
            </button>
          </div>
          <fieldset className="role-picks">
            <legend>Roles</legend>
            {choices.map((r) => (
              <label key={r.key} className="role-pick" title={r.description}>
                <input type="checkbox" checked={picked.includes(r.key)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, r.key] : p.filter((k) => k !== r.key)))} />
                <span>{r.name}</span>
              </label>
            ))}
          </fieldset>
          <p className="muted small">You can only give roles whose capabilities you hold there yourself. Email is not sent yet: the invitation is logged and the person signs in at Studio with their address.</p>
        </form>
      ) : null}
      <div className="toolbar">
        <SearchBox value={q} onChange={(value) => setQuery({ q: value || null }, { replace: true })} placeholder="Search people" />
      </div>
      {people.error ? <ErrorBox error={people.error} onRetry={people.reload} /> : null}
      {!people.data && people.loading ? <Loading /> : null}
      {people.data && people.data.users.length === 0 ? <Empty>Nobody matches.</Empty> : null}
      {people.data && people.data.users.length ? (
        <table className="table compact" aria-label="People">
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col">Roles per scope</th>
              <th scope="col">Last sign-in</th>
            </tr>
          </thead>
          <tbody>
            {people.data.users.map((person) => (
              <tr key={person.email}>
                <th scope="row">
                  {person.name}
                  <small className="block muted">{person.email}</small>
                  {person.status !== 'active' ? <span className="badge">inactive</span> : null}
                  {catalog ? <CapabilityMatrix person={person} catalog={catalog} /> : null}
                </th>
                <td>
                  <ul className="access-list">
                    {person.memberships.map((m) => (
                      <MembershipRoles key={m.id} person={person} membership={m} choices={rolesFor(catalog, m.organization.ref)} onSaved={people.reload} />
                    ))}
                  </ul>
                </td>
                <td className="small">{since(person.lastLoginAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </>
  );
}

/** Role chips on one scope: adding saves at once, taking a role or the whole access away asks once. */
function MembershipRoles({ person, membership: m, choices, onSaved }: { person: StudioPerson; membership: Membership; choices: RoleInfo[]; onSaved: () => void }) {
  const toast = useToast();
  const name = (key: string) => choices.find((r) => r.key === key)?.name ?? key;
  const addable = choices.filter((r) => !m.roles.includes(r.key));
  const setRoles = (roles: string[], message: string) => runWrite(toast, 'admin.users.setRoles', { membershipId: m.id, roles }, message).then((ok) => (ok ? ok : onSaved()));
  return (
    <li>
      <span className="scope-name">{scopeLabel(m)}</span>
      <span className="role-chips">
        {m.roles.map((key) =>
          m.canChange && m.roles.length > 1 ? (
            <ConfirmButton
              key={key}
              className="chip chip-removable"
              label={<>{name(key)} ×</>}
              title={`Take ${name(key)} away from ${person.email}?`}
              confirmLabel="Take role away"
              onConfirm={() => setRoles(m.roles.filter((r) => r !== key), `${person.email} no longer has ${name(key)} on ${scopeLabel(m)}`)}
            >
              <p>
                {person.name} keeps {m.roles.filter((r) => r !== key).map(name).join(', ')} on {scopeLabel(m)}.
              </p>
            </ConfirmButton>
          ) : (
            <span key={key} className="chip">
              {name(key)}
            </span>
          ),
        )}
      </span>
      {m.canChange && addable.length ? (
        <select aria-label={`Add a role for ${person.email} on ${scopeLabel(m)}`} value="" onChange={(e) => e.target.value && void setRoles([...m.roles, e.target.value], `${person.email} now also has ${name(e.target.value)} on ${scopeLabel(m)}`)}>
          <option value="">+ Add role</option>
          {addable.map((r) => (
            <option key={r.key} value={r.key} title={r.description}>
              {r.name}
            </option>
          ))}
        </select>
      ) : null}
      {m.canChange ? (
        <ConfirmButton label="Remove" title={`Remove ${person.email} from ${scopeLabel(m)}?`} confirmLabel="Remove access" onConfirm={() => runWrite(toast, 'admin.users.remove', { membershipId: m.id }, 'Access removed')}>
          <p>
            {person.name} loses {m.roleNames} on {scopeLabel(m)}. Their roles elsewhere stay.
          </p>
        </ConfirmButton>
      ) : null}
    </li>
  );
}

/** What a person may do, per capability and scope: R read, W write, A approve. */
function CapabilityMatrix({ person, catalog }: { person: StudioPerson; catalog: RolesCatalog }) {
  const held = (m: Membership, c: CapabilityInfo) => {
    const levels = c.levels.filter((level) => m.capabilities.includes(`${c.key}:${level}`));
    const top = levels.includes('approve') ? 'approve' : levels.includes('write') ? 'write' : levels[0];
    return top ? LEVEL_MARK[top] : '';
  };
  const rows = catalog.capabilities.filter((c) => person.memberships.some((m) => held(m, c)));
  return (
    <details className="capability-matrix">
      <summary className="small">What they can do</summary>
      <table className="table compact matrix" aria-label={`Capabilities of ${person.email}`}>
        <thead>
          <tr>
            <th scope="col">Capability</th>
            {person.memberships.map((m) => (
              <th key={m.id} scope="col">
                {scopeLabel(m)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {catalog.areas.map((area) => {
            const inArea = rows.filter((c) => c.area === area.key);
            if (!inArea.length) return null;
            return [
              <tr key={area.key} className="matrix-area">
                <th colSpan={person.memberships.length + 1} scope="colgroup">
                  {area.label}
                </th>
              </tr>,
              ...inArea.map((c) => (
                <tr key={c.key}>
                  <th scope="row" title={c.description}>
                    {c.label}
                  </th>
                  {person.memberships.map((m) => (
                    <td key={m.id} className="matrix-cell">
                      {held(m, c)}
                    </td>
                  ))}
                </tr>
              )),
            ];
          })}
        </tbody>
      </table>
      <p className="muted small">R read · W change · A approve. Roles on an organization or brand also cover its sites.</p>
    </details>
  );
}

function RolesTab({ catalog, loading }: { catalog: RolesCatalog | undefined; loading: boolean }) {
  const toast = useToast();
  const [editing, setEditing] = useState<CustomRoleInfo | 'new' | null>(null);
  if (!catalog) return loading ? <Loading /> : null;
  const manageable = catalog.organizations.filter((o) => o.canManageRoles);
  return (
    <>
      <h2 className="section-title">Role templates</h2>
      <table className="table compact" aria-label="Role templates">
        <thead>
          <tr>
            <th scope="col">Role</th>
            <th scope="col">What it allows</th>
          </tr>
        </thead>
        <tbody>
          {catalog.templates.map((t) => (
            <tr key={t.key}>
              <th scope="row">{t.name}</th>
              <td>
                {t.description}
                <small className="block muted">{summarize(t.capabilities, catalog)}</small>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="section-head">
        <h2 className="section-title">Custom roles</h2>
        {manageable.length ? (
          <button type="button" className="btn btn-small" onClick={() => setEditing('new')}>
            New role
          </button>
        ) : null}
      </div>
      {catalog.customRoles.length === 0 ? <Empty>No custom roles yet. Make one when a template gives too much or too little.</Empty> : null}
      {catalog.customRoles.length ? (
        <table className="table compact" aria-label="Custom roles">
          <thead>
            <tr>
              <th scope="col">Role</th>
              <th scope="col">Organization</th>
              <th scope="col">What it allows</th>
              <th scope="col">People</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {catalog.customRoles.map((r) => (
              <tr key={r.ref}>
                <th scope="row">
                  {r.name}
                  {r.description ? <small className="block muted">{r.description}</small> : null}
                </th>
                <td>{r.organization.name}</td>
                <td className="small">{summarize(r.capabilities, catalog)}</td>
                <td>{r.members}</td>
                <td className="actions">
                  {r.canEdit ? (
                    <>
                      <button type="button" className="btn btn-small btn-ghost" onClick={() => setEditing(r)}>
                        Edit
                      </button>
                      <ConfirmButton label="Delete" title={`Delete ${r.name}?`} confirmLabel="Delete role" onConfirm={() => runWrite(toast, 'admin.roles.delete', { ref: r.ref }, `${r.name} deleted`)}>
                        <p>
                          {r.members ? `${r.members} membership${r.members === 1 ? '' : 's'} lose this role; people left without any role on that scope lose access there.` : 'Nobody has this role.'}
                        </p>
                      </ConfirmButton>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {editing ? <RoleEditor catalog={catalog} role={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

/** "Content: Coupons W, Merchants R · Insights: Revenue R" */
function summarize(capabilities: readonly string[], catalog: RolesCatalog): string {
  const set = new Set(capabilities);
  return catalog.areas
    .map((area) => {
      const parts = catalog.capabilities
        .filter((c) => c.area === area.key)
        .map((c) => {
          const top = ['approve', 'write', 'read'].find((level) => set.has(`${c.key}:${level}`));
          return top ? `${c.label} ${LEVEL_MARK[top]}` : null;
        })
        .filter(Boolean);
      return parts.length ? `${area.label}: ${parts.join(', ')}` : null;
    })
    .filter(Boolean)
    .join(' · ');
}

function RoleEditor({ catalog, role, onClose }: { catalog: RolesCatalog; role: CustomRoleInfo | null; onClose: () => void }) {
  const toast = useToast();
  const organizations = catalog.organizations.filter((o) => o.canManageRoles);
  const [organizationRef, setOrganizationRef] = useState(role?.organization.ref ?? organizations[0]?.ref ?? '');
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [capabilities, setCapabilities] = useState<Set<string>>(new Set(role?.capabilities ?? []));
  const operator = catalog.organizations.find((o) => o.ref === organizationRef)?.platformOperator ?? false;
  const toggle = (value: string, on: boolean) =>
    setCapabilities((current) => {
      const next = new Set(current);
      if (on) next.add(value);
      else next.delete(value);
      return next;
    });
  const save = async () => {
    const list = [...capabilities].filter((c) => operator || !catalog.capabilities.find((d) => c.startsWith(`${d.key}:`))?.operatorOnly);
    const ok = role
      ? await runWrite(toast, 'admin.roles.update', { ref: role.ref, name, description, capabilities: list }, `${name} saved`)
      : await runWrite(toast, 'admin.roles.create', { organizationRef, name, description, capabilities: list }, `${name} created`);
    if (ok) onClose();
  };
  return (
    <Modal
      title={role ? `Edit ${role.name}` : 'New role'}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={name.trim().length < 2 || capabilities.size === 0}>
            {role ? 'Save role' : 'Create role'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        {role ? null : (
          <label>
            Organization
            <select value={organizationRef} onChange={(e) => setOrganizationRef(e.target.value)}>
              {organizations.map((o) => (
                <option key={o.ref} value={o.ref}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Name
          <input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="e.g. Brand partnerships lead" />
        </label>
        <label>
          Description
          <input value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
        </label>
      </div>
      <table className="table compact" aria-label="Capabilities">
        <tbody>
          {catalog.areas.map((area) => {
            const defs = catalog.capabilities.filter((c) => c.area === area.key && (operator || !c.operatorOnly));
            if (!defs.length) return null;
            return [
              <tr key={area.key} className="matrix-area">
                <th colSpan={4} scope="colgroup">
                  {area.label}
                </th>
              </tr>,
              ...defs.map((c) => (
                <tr key={c.key}>
                  <th scope="row" title={c.description}>
                    {c.label}
                  </th>
                  {['read', 'write', 'approve'].map((level) => (
                    <td key={level}>
                      {c.levels.includes(level) ? (
                        <label className="inline-field">
                          <input type="checkbox" checked={capabilities.has(`${c.key}:${level}`)} onChange={(e) => toggle(`${c.key}:${level}`, e.target.checked)} />
                          <span className="small">{level === 'write' ? 'change' : level}</span>
                        </label>
                      ) : null}
                    </td>
                  ))}
                </tr>
              )),
            ];
          })}
        </tbody>
      </table>
      <p className="muted small">Change and approve include read. You can only include capabilities you hold on the whole organization yourself.</p>
    </Modal>
  );
}
